import { useCallback, useState, useEffect, useMemo } from 'react';
import { fromJS } from 'immutable';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { isJourneyReport } from 'reporting-data/tsTypes/reportTypes';
import { isLoading, isSucceeded } from 'reporting-data/asyncData/AsyncData';
import FilterOverview from './FilterOverview';
import FilterEditor from './FilterEditor';
import { useFetchProperties } from './useFetchProperties';
import { buildPropertyFilters, constructNewReportFiltering, getTransformedPropertyFilterSetFromQuery } from './utils';
import { getAllowAnonymousVisitors, getJourneyDataTypeName, getJourneyObjectTypeId } from '../common/journeyHelpers';
import { TEMP_PROPERTY_PREFIXES } from 'reporting-snowflake/funnel/utils/properties';
import { jsx as _jsx } from "react/jsx-runtime";
const JourneyPropertyFilterEditor = ({
  report,
  onChange
}) => {
  const journeyReport = report.toJS();
  const objectTypeId = getJourneyObjectTypeId(journeyReport);
  const builderObjectName = getJourneyDataTypeName(journeyReport);
  const propertyFilterSet = getTransformedPropertyFilterSetFromQuery(journeyReport);
  const disabled = getAllowAnonymousVisitors(journeyReport);
  const propertiesFetch = useFetchProperties(objectTypeId);
  const [activeEditingFilter, setActiveEditingFilter] = useState();
  useEffect(() => {
    if (disabled) {
      setActiveEditingFilter(undefined);
    }
  }, [disabled]);
  const availableProperties = useMemo(() => {
    if (isSucceeded(propertiesFetch)) {
      const objectProperties = propertiesFetch.data;
      return Object.keys(objectProperties).filter(currentProperty => {
        const shouldRemove = TEMP_PROPERTY_PREFIXES.some(prefix => currentProperty.startsWith(prefix));
        return !shouldRemove;
      }).reduce((acc, currentProperty) => {
        acc[currentProperty] = objectProperties[currentProperty];
        return acc;
      }, {});
    }
    return undefined;
  }, [propertiesFetch]);
  const updateFilterByIndex = useCallback((newFilter, index) => {
    const {
      filters
    } = propertyFilterSet;
    const precedingFilters = filters.slice(0, index);
    const followingFilters = filters.slice(index + 1);
    const newFilterSet = {
      filters: newFilter ? [...precedingFilters, newFilter, ...followingFilters] : [...precedingFilters, ...followingFilters]
    };
    if (isSucceeded(propertiesFetch)) {
      const newFilterGroup = newFilterSet.filters.length > 0 ? {
        id: 2,
        condition: 'AND',
        filters: buildPropertyFilters(newFilterSet, propertiesFetch.data, builderObjectName)
      } : undefined;
      const updatedReport = report.setIn(['journeyQuery', 'objectQuery', 'filtering'], fromJS(constructNewReportFiltering(report.toJS(), {
        properties: newFilterGroup,
        deleteProperties: !newFilterGroup
      })));
      onChange(updatedReport);
    }
    setActiveEditingFilter(undefined);
  }, [propertyFilterSet, propertiesFetch, builderObjectName, report, onChange, setActiveEditingFilter]);
  const renderFilterContent = () => {
    if (isLoading(propertiesFetch)) {
      return /*#__PURE__*/_jsx(LoadingSpinner, {});
    }
    if (isSucceeded(propertiesFetch)) {
      return activeEditingFilter !== undefined ? /*#__PURE__*/_jsx(FilterEditor, {
        objectTypeId: objectTypeId,
        properties: availableProperties || {},
        currentFilter: propertyFilterSet.filters[activeEditingFilter],
        apply: filter => {
          updateFilterByIndex(filter, activeEditingFilter);
        },
        cancel: () => setActiveEditingFilter(undefined)
      }) : /*#__PURE__*/_jsx(FilterOverview, {
        objectTypeId: objectTypeId,
        properties: propertiesFetch.data,
        filterSet: propertyFilterSet,
        setActiveEditingFilter: setActiveEditingFilter,
        disabled: disabled,
        deleteFilter: index => updateFilterByIndex(undefined, index)
      });
    }
  };
  return /*#__PURE__*/_jsx(FormControl, {
    label: /*#__PURE__*/_jsx(FormattedMessage, {
      message: `settingLabels.property.label.${builderObjectName}`
    }),
    "data-test-id": "property-filter-header",
    children: renderFilterContent()
  });
};
JourneyPropertyFilterEditor.isUsableForReport = report => isJourneyReport(report);
export default JourneyPropertyFilterEditor;